import {
  SingleAutocomplete,
  domainCodeAtom,
  useSaladBar,
} from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import Search from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Dispatch, SetStateAction } from 'react';

import callBackendApi from '@/apis/call';
import { ElectionRecord, getElectionsContaining } from '@/apis/elections';
import { getListAddressCount } from '@/apis/lists';
import { environment } from '@/config';
import { labelSorting } from '@/util/labelSorting';

export const usePrefetchUpcomingElection = () => {
  const domainCode = useAtomValue(domainCodeAtom);
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  if (environment !== 'test')
    queryClient.ensureQueryData({
      queryKey: ['upcomingElection', domainCode],
      queryFn: async () => {
        const token = await getAccessTokenSilently();
        return callBackendApi({
          endpoint: `/elections/upcoming?domainCode=${domainCode}`,
          method: 'get',
          token,
        }) as Promise<ElectionRecord>;
      },
    });
};

export function useDistributionListAddressCount({
  listId,
  sortColumn,
  sortOrder,
  contactIds,
}: {
  listId: number;
  sortColumn: string;
  sortOrder: string;
  contactIds?: number[];
}) {
  const domainCode = useAtomValue(domainCodeAtom);
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['addressCount', listId, domainCode, sortColumn, sortOrder, contactIds],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getListAddressCount({
        token,
        listId,
        domainCode,
        sortColumn,
        sortOrder,
        contactIds,
      });
    },
    staleTime: 300000,
  });
}

export function useDownloadPdf(
  electionId: number,
  listId: string,
  sortOrder: string,
  sortColumn: string,
  notes: string
) {
  const domainCode = useAtomValue(domainCodeAtom);
  const { getAccessTokenSilently } = useAuth0();
  const { enqueueSuccessNotification } = useSaladBar();
  return async (contactIds?: number[]) => {
    const queryStrParts = [
      `domainCode=${domainCode}`,
      `election=${electionId}`,
      `sortOrder=${sortOrder}`,
      `sortColumn=${sortColumn}`,
      `notes=${encodeURIComponent(notes)}`,
    ];

    if (contactIds)
      queryStrParts.push(`contactIds=${encodeURIComponent(JSON.stringify(contactIds))}`);

    const pdfBlob: Blob = await callBackendApi({
      endpoint: `/lists/${listId}/distributionlist?${queryStrParts.join('&')}`,
      method: 'get',
      token: await getAccessTokenSilently(),
    });

    const link = document.createElement('a');
    link.style.display = 'none';
    link.download = `${new Date().toLocaleDateString()}-${electionId}-distribution-list-${listId}.pdf`;
    link.href = window.URL.createObjectURL(pdfBlob);
    link.click();
    link.remove();
    enqueueSuccessNotification('Pdf Downloaded');
  };
}

export default function DistributionListPanel({
  isDownloadPdfChecked,
  setIsDownloadPdfChecked,
  distributionListElection,
  setDistributionListElection,
  distributionListNotes,
  setDistributionListNotes,
  listCount,
  listId,
  sortOrder,
  sortColumn,
  contactIds,
}: {
  isDownloadPdfChecked: boolean;
  setIsDownloadPdfChecked: Dispatch<SetStateAction<boolean>>;
  distributionListElection: { id: number; label: string; shortName: string };
  setDistributionListElection: Dispatch<
    SetStateAction<{ id: number; label: string; shortName: string }>
  >;
  distributionListNotes: string;
  setDistributionListNotes: Dispatch<SetStateAction<string>>;
  listCount: number;
  listId: number;
  sortOrder: string;
  sortColumn: string;
  contactIds?: number[];
}) {
  const domainCode = useAtomValue(domainCodeAtom);
  const { getAccessTokenSilently } = useAuth0();

  const lookupByElectionName = async (lookupValue: string) => {
    const token = await getAccessTokenSilently();
    const returnData = await getElectionsContaining(token, lookupValue, domainCode);
    return returnData
      .map((x) => ({ id: x.id, label: x.name, shortName: x.shortname }))
      .sort(labelSorting);
  };

  const maxNotesChars = 600;

  const { data: contactsWithAddressCount } = useDistributionListAddressCount({
    listId,
    sortColumn,
    sortOrder,
    contactIds,
  });

  const onPDFDownloadCheckedChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    newChecked: boolean
  ) => {
    setIsDownloadPdfChecked(newChecked);
  };

  const missingAddressMessage =
    contactIds && contactIds.length > 0
      ? 'One or more selected contacts is missing an address.'
      : 'One or more contacts in this list is missing an address.';

  const allMissingAddressesMessage =
    contactIds && contactIds.length > 0
      ? 'All selected contacts are missing addresses.'
      : 'All contacts in this list are missing addresses.';

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: '16px'
      }}>
      <Typography>
        Generate a list to be used for sign distribution. Task notes for the selected election will
        be included.
      </Typography>
      <FormControlLabel
        control={<Checkbox value={isDownloadPdfChecked} onChange={onPDFDownloadCheckedChange} />}
        label="Download PDF"
      />
      {isDownloadPdfChecked && contactsWithAddressCount !== undefined && (
        <>
          {(contactIds && contactIds.length > 0
            ? contactIds.length > 100
            : contactsWithAddressCount > 0 && listCount > 100) && (
            <FormHelperText error>Only the first 100 contacts will be exported.</FormHelperText>
          )}
          {(contactIds
            ? contactIds.length !== contactsWithAddressCount
            : listCount !== contactsWithAddressCount) &&
            contactsWithAddressCount > 0 && (
              <FormHelperText error>{missingAddressMessage}</FormHelperText>
            )}
          {contactsWithAddressCount === 0 && (
            <FormHelperText error>{allMissingAddressesMessage}</FormHelperText>
          )}
        </>
      )}
      <SingleAutocomplete
        sx={{ width: '100%' }}
        onChange={(e, newElection) =>
          setDistributionListElection(newElection ?? { id: -1, label: '', shortName: '' })
        }
        value={distributionListElection}
        label={'Select election'}
        lookup={lookupByElectionName}
        hideButton
        disabled={!isDownloadPdfChecked}
        popupIcon={<Search />}
        disableIconFlip
      />
      <TextField
        multiline
        disabled={!isDownloadPdfChecked}
        label={`Add notes (max ${maxNotesChars} characters)`}
        variant="filled"
        value={distributionListNotes}
        onChange={(event) => {
          if (event.target.value.length > maxNotesChars) return;
          setDistributionListNotes(event.target.value);
        }}
      />
    </Box>
  );
}
